'use client';
/* Built In Imports */
import { useState } from 'react';

/* External Imports */
import { Link } from '@chakra-ui/next-js';
import { Box, Button, SimpleGrid, Text } from '@chakra-ui/react';

/* Internal Imports */
/* Components */
import {
  ArticleIcon,
  SpeakerIcon,
  VideoIcon,
} from '@app-ps/Components/Icon/wisdomCardIcon';
import LazyLoadImageComponent from '@app-ps/Components/Ui/LazyLoadImageComponent';
import { refineUrlDomain } from '@app-ps/Components/Utility/utils';
/**
 * @param {Object} sectionContent - Data from api
 * @param {Object} region - Data from api
 * @param {Object} lang - Data from api
 * @returns
 */
const BlogCards = ({ sectionContent, region, lang }) => {
  // Extract card data from sectionContent
  const cardData = sectionContent?.blogsList;

  // State to manage the number of displayed cards
  const [visibleCards, setVisibleCards] = useState(9);

  // Handle the "Load More" button click
  const handleLoadMore = () => {
    setVisibleCards(cardData.length); // Show all cards
  };

  const iconToShow = {
    article: (
      <ArticleIcon
        pt="0"
        color="white"
        width={{ base: '14px', md: '20px' }}
        height={{ base: '14px', md: '20px' }}
        viewBox={{ base: '0 0 23px 23px', md: '0 0 36px 36px' }}
      />
    ),
    audio: <SpeakerIcon />,
    video: (
      <VideoIcon
        pt="0"
        width={{ base: '23px', md: '36px' }}
        height={{ base: '23px', md: '36px' }}
        viewBox={{ base: '0 0 23px 23px', md: '0 0 36px 36px' }}
      />
    ),
  };

  // console.log('sdsd', sectionContent);

  return (
    <Box
      pt={{ lg: '4rem', base: '4rem' }}
      mb={{ base: '-6rem', md: '0' }}
      color={'white'}
      overflowX={{ base: 'hidden' }}
      width={'100%'}
      bg={'#010509'}
      px={{ base: '1rem', lg: '8rem', xl: '0', '2xl': '8rem' }}
    >
      {/* top-text box */}
      <Box
        display={'flex'}
        alignItems={'center'}
        gap={{ base: '1.5rem' }}
        flexDirection={'column'}
        marginX={{ lg: 'auto' }}
        textAlign={'center'}
        maxWidth={'715px'}
      >
        <Text
          fontSize={{ base: '1.25rem', md: '1.5rem' }}
          textAlign={{ base: 'left', md: 'center' }}
          fontFamily="var(--FedraSerifDisplay-light)"
          color="#F1EDE9"
          lineHeight={{ base: '1.5rem', md: '1.8rem' }}
          letterSpacing={'0.16px'}
        >
          Unveiling the Timeless Wisdom of India's Ancient Arts
        </Text>
        <Text
          fontWeight={350}
          fontSize={{ base: '0.875rem', md: '1.125rem' }}
          paddingRight={{ base: '.5rem', md: '0' }}
          fontFamily="var(--FedraSansStd-book)"
          lineHeight={{ base: '1.4rem', md: '1.8rem' }}
          letterSpacing={'0.32px'}
          color="#F1EDE9"
          // opacity={'0.7'}
        >
          Explore the depths of India's spiritual heritage through Sadhguru’s
          wisdom and a tapestry of stories delving into Indian classical music,
          dance and martial arts, which have been meticulously preserved for
          millennia.
        </Text>
      </Box>

      {/* cards grid */}
      <SimpleGrid
        columns={{ base: 1, sm: 2, lg: 3 }}
        spacing={{ base: '1.5rem', lg: '2.5rem', xl: '2rem', '2xl': '2rem' }}
        marginX={{ xl: '5rem', '2xl': '5rem' }}
        mt={{ base: '3rem', lg: '2.5rem' }}
      >
        {cardData.slice(0, visibleCards)?.map((card, index) => (
          <Box
            key={index}
            maxWidth={{ lg: '469px' }}
            flexDirection={'column'}
            gap={{ base: '1rem', lg: '1.2rem' }}
            mb="5px"
          >
            <Link
              href={refineUrlDomain(`blog/${card.urlAlias}`, region, lang)}
              _hover={{
                textDecoration: 'none',
              }}
              prefetch
            >
              <>
                {/* box for image  */}
                <Box
                  height={{ lg: 'auto' }}
                  // width={{ xl: '370px', '2xl': '468px' }}
                  w="100%"
                  maxWidth={{ xl: '469px' }}
                >
                  <LazyLoadImageComponent
                    src={card.heroImage?.url}
                    alt={card.heroImage?.alt}
                    width={{ xl: '100%', '2xl': '100%' }}
                    height={'100%'}
                    objectFit={{ base: 'cover', lg: 'fill', '2xl': 'cover' }}
                    objectPosition="center"
                  />
                </Box>
                {/* text-1 */}
                <Text
                  fontWeight={400}
                  mt={{ lg: '1rem', base: '.5rem' }}
                  fontSize={{ base: '0.875rem' }}
                  fontFamily="var(--FedraSansStd-book)"
                  lineHeight={{ base: '1.4rem' }}
                  letterSpacing={'0.32px'}
                  opacity={'0.8'}
                  textTransform={'capitalize'}
                  display="flex"
                >
                  {iconToShow[card.types]} {card.types} | {card.category}
                </Text>
                {/* text-2 */}
                <Text
                  fontWeight={500}
                  color="#FEFAF6"
                  mt={{ base: '1rem' }}
                  fontSize={{ base: '1rem', md: '1.25rem' }}
                  fontFamily="var(--FedraSansStd-medium)"
                  lineHeight={{ base: '1.6rem', md: '2rem' }}
                  letterSpacing={'0.32px'}
                >
                  {card.title}
                </Text>
                {/* text-3 */}

                <Text
                  fontWeight={400}
                  css={{
                    marginTop: '1.2rem',
                    padding: '0',
                  }}
                  fontSize={{ base: '0.75rem', md: '1rem' }}
                  fontFamily="var(--FedraSansStd-book)"
                  lineHeight={{ base: '1.2rem', md: '1.6rem' }}
                  letterSpacing={'0.32px'}
                  opacity={'0.8'}
                  color="#FEFAF6"
                >
                  {card.summary}
                </Text>
              </>
            </Link>
          </Box>
        ))}
      </SimpleGrid>

      {/* Load More button */}
      {visibleCards < cardData.length && (
        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          mb={{ base: '2rem', md: '5rem' }}
        >
          <Button
            bg={'#60ADFF'}
            border={'none'}
            minWidth={'167px'}
            minHeight={'50px'}
            borderRadius={'none'}
            _hover={{
              bgColor: '#80BDFF', // Sets the background color on hover
            }}
            fontWeight={500}
            paddingX={{ base: '2rem' }}
            zIndex={10}
            paddingY={{ base: '0.75rem' }}
            mt={{ base: '1rem', lg: '4rem' }}
            fontSize={{ base: '1rem' }}
            fontFamily="var(--FedraSansStd-medium)"
            lineHeight={{ base: '1.6rem', md: '2rem' }}
            color="#010509"
            letterSpacing={'0.32px'}
            onClick={handleLoadMore}
          >
            LOAD MORE
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default BlogCards;
